import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import Logo from "@/components/Logo";

const LINKS = [
  { to: "/services/meta-ads", label: "Meta Ads", id: "nav-link-meta-ads" },
  { to: "/services/google-ads", label: "Google Ads", id: "nav-link-google-ads" },
  { to: "/contact", label: "Contact", id: "nav-link-contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 border-b ${
          scrolled
            ? "bg-obsidian/85 backdrop-blur-md border-white/10"
            : "bg-obsidian/40 backdrop-blur-sm border-transparent"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="nav-brand-logo" aria-label="Vibera Group home">
            <Logo />
          </Link>
          <div className="hidden md:flex items-center gap-9">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.id}
                className={({ isActive }) =>
                  `text-sm transition-colors ${isActive ? "text-flame" : "text-gray-300 hover:text-white"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-cta-audit"
              className="group inline-flex items-center gap-2 rounded-full bg-flame px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-flame-hover"
            >
              Book Strategic Audit
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
          <button
            className="md:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 top-[72px] z-40 bg-obsidian md:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="pt-10 px-6 flex flex-col">
              {[{ to: "/", label: "Home", id: "nav-link-home" }, ...LINKS].map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.35 }}
                >
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    data-testid={l.id}
                    className={({ isActive }) =>
                      `block py-4 font-syne text-3xl font-bold border-b border-white/10 ${
                        isActive ? "text-flame" : "text-white"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
                <Link
                  to="/contact"
                  data-testid="nav-mobile-cta"
                  className="mt-8 flex justify-center items-center gap-2 rounded-full bg-flame px-6 py-4 font-semibold text-white"
                >
                  Book Strategic Audit
                  <ArrowUpRight className="h-5 w-5" />
                </Link>
                <a href="tel:+13075007253" data-testid="nav-mobile-phone" className="mt-5 block text-center font-mono text-sm text-gray-400">
                  +1 (307) 500-7253
                </a>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
